import React, { Suspense, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { MarketButton } from '@market/react';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import AddressSuggestions from 'components/common/address_suggestions';
import AccessPointSelection from 'components/common/access_point_selection';
import ControlledCheckbox from 'components/common/forms/controlled_checkbox';
import NameFields from 'components/common/name_fields';
import { fetchLocalizedComponent } from 'utilities/component_localization';
import {
  ACCESS_POINT_SEARCH,
  ADDRESS_DISCLAIMER,
  ADDRESS_SUGGESTION,
  isFeatureEnabled,
} from 'utilities/features';
import { buildLegalLink } from 'utilities/links';

const AddressFields = fetchLocalizedComponent(
  'common/information/address_fields',
  { es: 'eu', fr: 'eu' },
);

const ADDRESS_TYPE = Object.freeze({
  SHIPPING: 'shipping',
  BILLING: 'billing',
});

const BILLING_PREFIX = 'billing_';

const AddressForm = ({ handleSubmit, disableCta }) => {
  const { t } = useTranslation();
  const { store } = useOrderDetails();
  const { control, setValue } = useFormContext();
  const [accessPointModalOpen, setAccessPointModalOpen] = useState(false);
  const {
    addressClient,
    orderData,
    shippingAddress,
    setShippingAddress,
    billingAddress,
    setBillingAddress,
  } = useCheckoutFlow();
  const [isAccessPoint, setIsAccessPoint] = useState(
    !orderData.shipping_address.editable,
  );
  const isWarranty = orderData.is_warranty;
  const [sameBillingAsShipping, setSameBillingAsShipping] = useState(
    JSON.stringify(orderData.shipping_address) ===
      JSON.stringify(orderData.billing_address),
  );

  const handleSetIsAccessPoint = (value) => {
    // Ensure that component state and form state are always in sync
    setIsAccessPoint(value);
    setValue('is_access_point', value);
  };

  const handleSuggestionSelected = (placeID, searchInput, addressType) => {
    const setAddress =
      addressType === ADDRESS_TYPE.BILLING
        ? setBillingAddress
        : setShippingAddress;
    if (placeID === 'user-input') {
      setAddress({ address_line_1: searchInput });
    } else {
      addressClient
        .placeDetails(placeID)
        .then((data) => {
          const address = data.address;
          // Places API returns GB state data in the administrative_district_level_2 field
          if (store === 'GB') {
            address.administrative_district_level_1 =
              address.administrative_district_level_2;
          }
          setAddress(address);
        })
        .catch(() => setAddress({ address_line_1: searchInput }));
    }
    handleSetIsAccessPoint(false);
  };

  const showAddressSuggestions = (addressType) => {
    const address =
      addressType === ADDRESS_TYPE.BILLING ? billingAddress : shippingAddress;
    return (
      Object.keys(address).length === 0 && isFeatureEnabled(ADDRESS_SUGGESTION)
    );
  };

  const addressInput = (address, addressType) => {
    const namePrefix =
      addressType === ADDRESS_TYPE.BILLING ? BILLING_PREFIX : '';

    return showAddressSuggestions(addressType) ? (
      <AddressSuggestions
        handleSuggestionSelected={handleSuggestionSelected}
        addressType={addressType}
      />
    ) : (
      <Suspense fallback={null}>
        <AddressFields
          address={address}
          countryCode={store}
          disabled={isAccessPoint && isFeatureEnabled(ACCESS_POINT_SEARCH)}
          namePrefix={namePrefix}
        />
      </Suspense>
    );
  };

  // Since address line 3 is used for company, don't update it from access point selection.
  const removeAddressLine3FromAccessPoint = (accessPoint) => {
    const accessPointCopy = { ...accessPoint };
    delete accessPointCopy.address_line_3;
    return accessPointCopy;
  };

  const accessPoint = () =>
    isFeatureEnabled(ACCESS_POINT_SEARCH) ? (
      <>
        <div style={{ display: 'none' }}>
          <ControlledCheckbox
            name="is_access_point"
            control={control}
            defaultValue={isAccessPoint}
          />
        </div>
        {Object.keys(shippingAddress).length === 0 ? (
          <Grid item xs={12} md={6}>
            <MarketButton
              id="access-point-modal-trigger-btn"
              onClick={() => setAccessPointModalOpen(true)}
              style={{ width: '100%' }}
            >
              {t('checkout.label.accessPointButton')}
            </MarketButton>
          </Grid>
        ) : (
          <Grid item xs={12} md={6}>
            <MarketButton
              id="access-point-edit-btn"
              onClick={() => {
                if (isAccessPoint) {
                  setShippingAddress({});
                  handleSetIsAccessPoint(false);
                } else {
                  setAccessPointModalOpen(true);
                }
              }}
              style={{ width: '100%' }}
            >
              {isAccessPoint
                ? t('checkout.label.enterADifferentAddress')
                : t('checkout.label.searchForAccessPoints')}
            </MarketButton>
          </Grid>
        )}
        <AccessPointSelection
          open={accessPointModalOpen}
          onAddressSelect={(accessPoint) => {
            setShippingAddress(removeAddressLine3FromAccessPoint(accessPoint));
            handleSetIsAccessPoint(true);
            setAccessPointModalOpen(false);
          }}
          handleClose={() => setAccessPointModalOpen(false)}
        />
      </>
    ) : null;

  return (
    <>
      {addressInput(shippingAddress, ADDRESS_TYPE.SHIPPING)}
      {isFeatureEnabled(ADDRESS_DISCLAIMER) && (
        <Grid item xs={12}>
          <p className="paragraph-10">{t('checkout.address.disclaimer')}</p>
        </Grid>
      )}
      {accessPoint()}
      <Grid item xs={12}>
        <Box sx={{ pt: 2 }} component="h2">
          {t('checkout.information.billingTitle')}
        </Box>
        <ControlledCheckbox
          name="billing_same_as_shipping"
          control={control}
          defaultValue={sameBillingAsShipping}
          label={
            <label slot="label">
              {t('checkout.information.billingSameAsShipping')}
            </label>
          }
          onValueChange={setSameBillingAsShipping}
        />
      </Grid>
      {!sameBillingAsShipping && (
        <Grid item xs={12} container spacing={2} justifyContent="flex-end">
          <NameFields
            address={billingAddress}
            store={store}
            namePrefix={BILLING_PREFIX}
          />
          {addressInput(billingAddress, ADDRESS_TYPE.BILLING)}
        </Grid>
      )}
      <Grid item xs={12} md={isWarranty ? 12 : 6}>
        <Controller
          name="continue-to-shipping-btn"
          control={control}
          render={({ formState: { isSubmitting, isValidating } }) => (
            <MarketButton
              {...((showAddressSuggestions() ||
                isValidating ||
                isSubmitting ||
                disableCta) && {
                disabled: true,
              })}
              id="continue-to-shipping-btn"
              rank="primary"
              onClick={handleSubmit}
              style={{ width: '100%' }}
            >
              {isWarranty
                ? t('common.continue')
                : t('checkout.label.continueToShipping')}
            </MarketButton>
          )}
        />
      </Grid>
      {isWarranty && (
        <Grid item xs={12} container justifyContent="center">
          <p className="paragraph-20">
            <Trans i18nKey="checkout.label.viewPolicy">
              <a
                href={buildLegalLink('/hardware')}
                target="_blank"
                rel="noreferrer"
              />
            </Trans>
          </p>
        </Grid>
      )}
    </>
  );
};

export default AddressForm;
